/**
 * dcent-pdp-affiliate-header.js — 인플루언서 밴드의 **쿼리 모드** 채우기.
 *
 * 왜 JS 인가: 글로벌은 인플루언서 전원이 상품 하나를 공유해 누구인지가 URL 쿼리 bg_ref 에만 있다.
 * Shopify Liquid 은 쿼리 문자열을 못 읽으므로(request 객체에 없다) 서버에서는 고를 수 없다.
 * 캐시 실측으로도 확인됐다 — 같은 URL 의 서버 응답이 bg_ref 별로 갈리지 않았다.
 * 한국은 상품 handle 접미어로 서버에서 끝나므로 그 경우 섹션이 이 파일을 아예 내보내지 않는다.
 *
 * 가드는 라이브와 같다: 경로에 path_guard 포함 + bg_ref 존재 + 명단에 있는 코드.
 * 셋 중 하나라도 어긋나면 밴드는 hidden 인 채로 둔다(잘못된 인플루언서가 노출되는 쪽이 더 나쁘다).
 *
 * 값은 textContent 로만 넣는다 — 인용문에 든 따옴표·꺾쇠가 마크업으로 해석되지 않게 한다.
 * 양 샵 byte-identical.
 *
 * ── display="pick" (제휴 허브 배치안 B, 2026-09-29) ─────────────────────────
 * 밴드 대신 같은 명단 한 벌로 세 곳을 채운다. 섹션 자체는 끝까지 hidden 이다.
 *   ② 구매 박스 배너 — `.dcent-buy__options` 바로 앞에 끼운다. 구매 컬럼 스니펫은 모든 PDP 가
 *      공유하므로 거기에 슬롯을 두지 않고, 이 섹션이 있는 페이지에서만 DOM 으로 붙인다.
 *   ③ 추천 옵션 자동 선택 — 구매 박스의 옵션 버튼을 **대신 클릭**한다(chooser 와 같은 계약 —
 *      선택·URL 동기화·상태 방송은 dcent-pdp-buy.js 가 소유). URL 에 ?variant= 가 있으면
 *      그 사람이 고른 것이라 건드리지 않고 "Pick" 칩만 붙인다.
 *   ④ 비교 선택기 카드 배지 — 추천 카드에 배지 + is-aff-pick.
 * 코드는 URL bg_ref → 없으면 dcent_bixgrow 쿠키(dcent-bixgrow.liquid 가 7일 저장). 쿠키를 읽는
 * 이유는 두 번째 방문에도 화면이 실제 커미션 귀속과 같은 사람을 가리키게 하려는 것이다.
 * band 모드는 종전처럼 URL 만 본다(라이브 동작 유지).
 *
 * 타이밍: 이 파일(classic defer)은 템플릿상 히어로보다 앞이라 dcent-pdp-buy.js(module)보다
 * 먼저 실행된다. 옵션 클릭은 바인딩 뒤여야 먹히므로 DOMContentLoaded 에서 한다 — 지연 스크립트는
 * 전부 그 전에 실행되므로 그 시점엔 구매 박스가 묶여 있다.
 */
(function () {
  "use strict";

  var ROOT_SELECTOR =
    '[data-section-type="dcent-pdp-affiliate-header"][data-affiliate-mode="query"]';
  var READY_ATTR = "data-affiliate-ready";
  var COOKIE_NAME = "dcent_bixgrow";
  var REF_REGEX = /^[A-Za-z0-9_-]{4,64}$/;

  // 지연 스크립트는 DOMContentLoaded 전에 실행된다. 테마 에디터 재로드처럼 그 뒤에 불리는 경우를
  // 가리려고 플래그를 둔다(readyState 'interactive' 는 DCL 전후를 구별하지 못한다).
  var domReady = false;
  document.addEventListener("DOMContentLoaded", function () {
    domReady = true;
  });
  function whenReady(fn) {
    if (domReady || document.readyState === "complete") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  var norm = function (v) {
    return String(v == null ? "" : v).trim().toLowerCase();
  };

  function readCookie() {
    var m = document.cookie.match(new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]+)"));
    if (!m) return null;
    var v;
    try {
      v = decodeURIComponent(m[1]);
    } catch (err) {
      return null;
    }
    return REF_REGEX.test(v) ? v : null;
  }

  /** 섹션이 내보낸 명단 JSON. 없거나 깨졌으면 빈 배열 — 밴드는 숨은 채로 남는다. */
  function readList(root) {
    var node = root.querySelector(
      'script[type="application/json"][data-dcent-aff-map]',
    );
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function setText(root, selector, value) {
    var el = root.querySelector(selector);
    if (!el) return;
    el.textContent = value == null ? "" : String(value);
  }

  /** 명단에서 코드가 같은 항목. 없으면 null. */
  function findEntry(root, code) {
    if (!code) return null;
    var list = readList(root);
    for (var i = 0; i < list.length; i += 1) {
      if (list[i] && list[i].code === code) return list[i];
    }
    return null;
  }

  function fill(root) {
    if (root.getAttribute(READY_ATTR) === "1") return;

    var url;
    try {
      url = new URL(window.location.href);
    } catch (err) {
      return;
    }

    var guard = (root.getAttribute("data-path-guard") || "").trim();
    if (guard && url.pathname.indexOf(guard) === -1) return;

    if (root.getAttribute("data-display") === "pick") {
      var pickEntry =
        findEntry(root, url.searchParams.get("bg_ref")) || findEntry(root, readCookie());
      if (!pickEntry) return;
      root.setAttribute(READY_ATTR, "1");
      var hasVariant = url.searchParams.has("variant");
      whenReady(function () {
        applyPick(root, pickEntry, hasVariant);
      });
      return;
    }

    var entry = findEntry(root, url.searchParams.get("bg_ref"));
    if (!entry) return;

    var title = entry.title == null ? "" : String(entry.title);
    setText(root, ".dcent-pah__title", title);
    setText(root, ".dcent-pah__text1", entry.text1);
    setText(root, ".dcent-pah__text2", entry.text2);
    setText(root, ".dcent-pah__text3", entry.text3);

    var img = root.querySelector(".dcent-pah__logo-img");
    if (img) {
      if (entry.logo) {
        img.setAttribute("src", String(entry.logo));
        img.setAttribute("alt", title);
        img.removeAttribute("hidden");
      } else {
        // 로고가 없으면 컬럼째 접는다. img 만 지우면 컬럼에 공백 텍스트 노드가 남아
        // :empty 가 안 걸리고 PC 에서 빈 40% 가 그대로 자리를 차지한다.
        var col = img.closest(".dcent-pah__logo");
        if (col) col.setAttribute("hidden", "");
      }
    }

    root.setAttribute(READY_ATTR, "1");
    root.removeAttribute("hidden");
  }

  // ── display="pick" ─────────────────────────────────────────────────────

  /** 표시 이름 — display_name, 없으면 옛 제목 "DCENT 🤝 James Rule" 의 뒤쪽. */
  function displayName(entry) {
    var n = entry.name == null ? "" : String(entry.name).trim();
    if (n) return n;
    var t = entry.title == null ? "" : String(entry.title);
    var parts = t.split("\uD83E\uDD1D"); // 🤝
    return (parts.length > 1 ? parts[parts.length - 1] : t).trim();
  }

  function fmt(template, name) {
    return String(template || "").split("%NAME%").join(name);
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = String(text);
    return node;
  }

  /** 구매 박스 옵션 중 라벨에 recommends 가 든 첫 버튼 — chooser 의 findBuyOption 과 같은 규칙. */
  function findBuyOption(buy, recommends) {
    var m = norm(recommends);
    if (!m) return null;
    var buttons = buy.querySelectorAll("[data-dcent-buy-select]");
    for (var i = 0; i < buttons.length; i += 1) {
      if (norm(buttons[i].getAttribute("data-dcent-buy-select")).indexOf(m) !== -1) {
        return buttons[i];
      }
    }
    return null;
  }

  function applyPick(root, entry, hasVariant) {
    var name = displayName(entry);
    if (!name) return;
    var recommends = entry.recommends == null ? "" : String(entry.recommends).trim();
    var pickLabel = String(entry.pick || "").trim() || recommends;
    var pickTitle = fmt(root.getAttribute("data-pick-title") || "%NAME%'s pick", name);

    var buy = document.querySelector("[data-dcent-pdp-buy]");
    var option = buy ? findBuyOption(buy, recommends) : null;

    // ② 구매 박스 배너
    var anchor = buy ? buy.querySelector(".dcent-buy__options") : null;
    if (anchor && !buy.querySelector("[data-dcent-aff-pick]")) {
      var banner = el("div", "dcent-aff-pick");
      banner.setAttribute("data-dcent-aff-pick", "");
      var avatarSrc = String(entry.avatar || entry.logo || "").trim();
      if (avatarSrc) {
        var img = el("img", "dcent-aff-pick__avatar");
        img.setAttribute("src", avatarSrc);
        img.setAttribute("alt", "");
        img.setAttribute("width", "44");
        img.setAttribute("height", "44");
        img.setAttribute("decoding", "async");
        banner.appendChild(img);
      }
      var body = el("div", "dcent-aff-pick__body");
      var title = el("p", "dcent-aff-pick__title");
      if (recommends && pickLabel) {
        title.appendChild(el("span", "dcent-aff-pick__who", pickTitle + ": "));
        title.appendChild(el("strong", "dcent-aff-pick__what", pickLabel));
      } else {
        title.appendChild(
          el("span", "dcent-aff-pick__who", fmt(root.getAttribute("data-shop-with") || "Shopping with %NAME%", name)),
        );
      }
      body.appendChild(title);
      // 인플루언서별 할인 문구가 우선, 없으면 허브 공통 넛지(섹션 nudge_key).
      var discount = String(entry.discount || root.getAttribute("data-nudge") || "").trim();
      if (discount) body.appendChild(el("p", "dcent-aff-pick__discount", discount));
      var quote = String(entry.text1 || "").trim();
      if (quote) body.appendChild(el("p", "dcent-aff-pick__quote", quote));
      banner.appendChild(body);
      anchor.parentNode.insertBefore(banner, anchor);
    }

    // ③ 추천 옵션 — 칩은 늘 붙이고, 선택은 ?variant= 가 없을 때만 한다.
    if (option) {
      if (!option.querySelector("[data-dcent-aff-chip]")) {
        var chip = el("span", "dcent-aff-chip", root.getAttribute("data-pick-chip") || "Pick");
        chip.setAttribute("data-dcent-aff-chip", "");
        var main = option.querySelector(".dcent-buy__option-main") || option;
        main.appendChild(chip);
      }
      if (!hasVariant && option.getAttribute("aria-checked") !== "true") option.click();
    }

    // ④ 비교 선택기 카드 배지 — 카드 match 가 추천 옵션 라벨에 들어 있으면 추천 카드다.
    var pickedLabel = norm(option ? option.getAttribute("data-dcent-buy-select") : recommends);
    if (pickedLabel) {
      var cards = document.querySelectorAll("[data-dcent-chooser-card]");
      for (var i = 0; i < cards.length; i += 1) {
        var card = cards[i];
        var m = norm(card.getAttribute("data-match"));
        if (!m || pickedLabel.indexOf(m) === -1 || card.querySelector("[data-dcent-aff-badge]")) continue;
        card.classList.add("is-aff-pick");
        // 기존 배지(New 등)와 나란히 서도록 한 줄 컨테이너로 모은다.
        var row = el("div", "dcent-aff-badges");
        var existing = card.querySelector(".dcent-pac__badge");
        if (existing) row.appendChild(existing);
        var badge = el("span", "dcent-pac__badge dcent-aff-badge", pickTitle);
        badge.setAttribute("data-dcent-aff-badge", "");
        row.appendChild(badge);
        card.insertBefore(row, card.firstChild);
      }
    }

    document.dispatchEvent(
      new CustomEvent("dcent:affiliate", {
        detail: { code: entry.code, name: name, recommends: recommends },
      }),
    );
  }

  function run(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;
    var nodes = base.querySelectorAll(ROOT_SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) fill(nodes[i]);
    // 테마 에디터는 갱신된 섹션 자체를 target 으로 준다 — 그 자신이 루트일 수 있다.
    if (base.matches && base.matches(ROOT_SELECTOR)) fill(base);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      run(document);
    });
  } else {
    run(document);
  }

  document.addEventListener("shopify:section:load", function (evt) {
    run(evt.target || document);
  });
})();
